// The roll-the-fleet write state — the state half of `<FleetRolloutModal/>`,
// split from the panel the way `createMachineRemove` is.
//
// Three rules:
//
//   - **The channel is never defaulted.** It starts null and the trigger stays
//     shut until a human picks one. Both the route and the verb say so, and the
//     reason is that the two channels are not interchangeable: `canary` is the
//     slice a staged rollout soaks on, `stable` is everyone. A pre-selected
//     dropdown is a default by another name, so this holds `null` and the submit
//     reads `ready()`.
//   - **Changing the channel drops the pinned version.** A build is published
//     per channel, so a version carried across from the other one either 422s or
//     — worse — names a build that channel does not serve. The pin is part of
//     the channel choice, not a field beside it.
//   - **A 202 is not an updated fleet.** `started: false` means a rollout was
//     already running and this trigger joined it, and then the version reported
//     is the one ACTUALLY rolling. The outcome is rendered from the server's
//     answer, never from what was asked for.

import type { RunnerReleaseChannel } from '@developerz/domain';
import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { errorReason } from './api';
import { type RolloutStarted, startRollout } from './box-lifecycle';

export type RolloutForm = {
  /** The picked channel, or null — the state the trigger is shut in. */
  readonly channel: () => RunnerReleaseChannel | null;
  readonly pick: (channel: RunnerReleaseChannel | null) => void;
  /** The pinned version; `''` ⇒ the channel's newest installable build. */
  readonly version: () => string;
  readonly setVersion: (version: string) => void;
  /** True once a channel is picked — the only gate the client applies. */
  readonly ready: () => boolean;
  readonly pending: () => boolean;
  /** The API's refusal (including the 503 of a deployment with no rollout lane). */
  readonly error: () => string | null;
  /** The 202 body, once one lands. Rendered as the outcome. */
  readonly started: () => RolloutStarted | null;
  readonly submit: () => void;
  /** Back to a fresh, channel-less form — the dialog opening again. */
  readonly reset: () => void;
};

export function createRolloutForm(input: {
  readonly token: string | null;
  /** Fired once a rollout is accepted — the caller re-reads the version spread. */
  readonly onStarted: () => void;
}): RolloutForm {
  const [channel, setChannel] = createSignal<RunnerReleaseChannel | null>(null);
  const [version, setVersion] = createSignal('');
  const [pending, setPending] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [started, setStarted] = createSignal<RolloutStarted | null>(null);

  const submit = (): void => {
    const picked = channel();
    if (picked === null || pending()) return;
    setPending(true);
    setError(null);
    // The outcome belongs to the rollout that produced it. Changing only the pin
    // and firing again would otherwise leave the previous 202's alert on screen —
    // beside the new refusal, since the modal renders both blocks.
    setStarted(null);
    const pin = version().trim();
    void startRollout({ channel: picked, ...(pin === '' ? {} : { version: pin }) }, input.token)
      .then((result) => {
        setStarted(result);
        input.onStarted();
      })
      .catch((e: unknown) => setError(errorReason(e, t('fleet.rollout.failed'))))
      .finally(() => setPending(false));
  };

  return {
    channel,
    pick: (next) => {
      setChannel(next);
      // The pin belongs to the channel it was chosen under.
      setVersion('');
      setError(null);
      setStarted(null);
    },
    version,
    setVersion,
    ready: () => channel() !== null,
    pending,
    error,
    started,
    submit,
    reset: () => {
      setChannel(null);
      setVersion('');
      setError(null);
      setStarted(null);
    },
  };
}
